import { ReactNode } from 'react';

export function Card({ children, className = '' }: { children: ReactNode; className?: string }) {
  return (
    <div className={`rounded-2xl border border-slate-200 bg-white shadow-sm ${className}`}>
      {children}
    </div>
  );
}

export function SectionHeader({ title, subtitle, right }: { title: string; subtitle?: string; right?: ReactNode }) {
  return (
    <div className="flex items-start justify-between gap-4 mb-6">
      <div>
        <h1 className="text-2xl font-extrabold text-slate-900 tracking-tight">{title}</h1>
        {subtitle && <p className="text-slate-500 text-xs font-semibold mt-1 max-w-2xl">{subtitle}</p>}
      </div>
      {right}
    </div>
  );
}

type StatusTone = 'success' | 'warn' | 'danger' | 'info' | 'neutral';
const toneStyles: Record<StatusTone, string> = {
  success: 'bg-emerald-50 text-emerald-700 ring-1 ring-emerald-600/25',
  warn: 'bg-amber-50 text-amber-700 ring-1 ring-amber-600/25',
  danger: 'bg-red-50 text-red-700 ring-1 ring-red-600/25',
  info: 'bg-blue-50 text-blue-700 ring-1 ring-blue-600/25',
  neutral: 'bg-slate-50 text-slate-700 ring-1 ring-slate-200',
};

export function Badge({ children, tone = 'neutral' }: { children: ReactNode; tone?: StatusTone }) {
  return (
    <span className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-xs font-bold ${toneStyles[tone]}`}>
      {children}
    </span>
  );
}

export function StatusDot({ tone = 'neutral', pulse = false }: { tone?: StatusTone; pulse?: boolean }) {
  const colors: Record<StatusTone, string> = {
    success: 'bg-emerald-500',
    warn: 'bg-amber-500',
    danger: 'bg-red-500',
    info: 'bg-blue-500',
    neutral: 'bg-slate-400',
  };
  return <span className={`inline-block h-2 w-2 rounded-full ${colors[tone]} ${pulse ? 'pulse-dot' : ''}`} />;
}

export function Button({
  children,
  onClick,
  variant = 'primary',
  size = 'md',
  disabled = false,
  className = '',
  type = 'button',
}: {
  children: ReactNode;
  onClick?: () => void;
  variant?: 'primary' | 'ghost' | 'secondary' | 'danger';
  size?: 'sm' | 'md';
  disabled?: boolean;
  className?: string;
  type?: 'button' | 'submit';
}) {
  const base = 'inline-flex items-center gap-2 font-bold rounded-xl transition-all disabled:opacity-50 disabled:cursor-not-allowed';
  const sizes = { sm: 'text-xs px-3 py-1.5', md: 'text-sm px-4 py-2' };
  const variants = {
    primary: 'bg-emerald-600 hover:bg-emerald-700 text-white shadow-sm cursor-pointer',
    secondary: 'bg-white hover:bg-slate-50 text-slate-700 ring-1 ring-slate-200 cursor-pointer',
    ghost: 'text-slate-600 hover:text-slate-900 hover:bg-slate-100 cursor-pointer',
    danger: 'bg-red-600 hover:bg-red-700 text-white cursor-pointer',
  };
  return (
    <button type={type} onClick={onClick} disabled={disabled} className={`${base} ${sizes[size]} ${variants[variant]} ${className}`}>
      {children}
    </button>
  );
}

export function Stat({ label, value, delta, tone = 'neutral' }: { label: string; value: string | number; delta?: string; tone?: StatusTone }) {
  const deltaColor = tone === 'success' ? 'text-emerald-600' : tone === 'danger' ? 'text-red-600' : 'text-slate-500';
  return (
    <Card className="p-5">
      <div className="text-xs uppercase tracking-wider text-slate-400 font-extrabold">{label}</div>
      <div className="mt-2 text-3xl font-extrabold text-slate-900 tabular-nums">{value}</div>
      {delta && <div className={`mt-1 text-xs ${deltaColor} font-semibold`}>{delta}</div>}
    </Card>
  );
}

export function Empty({ title, hint }: { title: string; hint?: string }) {
  return (
    <div className="text-center py-16 text-slate-400">
      <div className="text-sm font-extrabold text-slate-700">{title}</div>
      {hint && <div className="text-xs font-semibold mt-1">{hint}</div>}
    </div>
  );
}

export function Skeleton({ className = '' }: { className?: string }) {
  return <div className={`shimmer rounded-md bg-slate-100 ${className}`} />;
}
